import React, { useEffect } from 'react';
import { Container, Title, Text, List, ThemeIcon, Card, createStyles, Anchor, Group, Stack, Badge } from '@mantine/core';
import { IconCheck, IconShieldCheck, IconLock, IconGavel, IconAlertCircle, IconTruck, IconCreditCard } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
      wrapper: {
            paddingTop: 80,
            paddingBottom: 80,
            minHeight: '100vh',
            backgroundColor: '#050505',
            backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
            color: theme.colors.gray[3],
            [theme.fn.smallerThan('sm')]: {
                  paddingTop: 60,
                  paddingBottom: 40,
            },
      },

      title: {
            fontSize: 34,
            fontWeight: 800,
            marginBottom: theme.spacing.xl,
            color: theme.white,
            textAlign: 'center',
            [theme.fn.smallerThan('sm')]: {
                  fontSize: 28,
                  marginBottom: theme.spacing.md,
            },
      },

      sectionTitle: {
            fontSize: 22,
            fontWeight: 700,
            marginTop: theme.spacing.xl * 1.5,
            marginBottom: theme.spacing.md,
            color: theme.colors.cyan[4],
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            marginTop: '35px',
            [theme.fn.smallerThan('sm')]: {
                  fontSize: 18,
                  marginTop: theme.spacing.lg,
            },
      },

      content: {
            fontSize: 16,
            lineHeight: 1.6,
            color: theme.colors.gray[4],
            [theme.fn.smallerThan('sm')]: {
                  fontSize: 14,
            },
      },

      card: {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            padding: theme.spacing.xl,
            borderRadius: theme.radius.lg,
            [theme.fn.smallerThan('sm')]: {
                  padding: theme.spacing.md,
            },
      },

      list: {
            marginTop: theme.spacing.sm,
            marginBottom: theme.spacing.sm,
            color: theme.colors.gray[3],
      }
}));

export default function TNC() {
      const { classes } = useStyles();

      useEffect(() => {
            window.scrollTo(0, 0);
      }, []);

      return (
            <div className={classes.wrapper}>
                  <Container size="md">
                        <Card className={classes.card}>
                              <Title className={classes.title}>Terms and Conditions</Title>

                              <Group position="center" mb="xl">
                                    <Badge size="lg" color="cyan">Effective Date: 06-02-2026</Badge>
                                    <Badge size="lg" variant="outline" color="gray">CDP3D</Badge>
                              </Group>

                              <Text className={classes.content}>
                                    Welcome to <strong>CDP3D</strong>. These Terms and Conditions (“Terms”) govern your use of our website <Anchor href="https://cdp3d.com" target="_blank" color="cyan">https://cdp3d.com</Anchor> and our 3D printing services. By accessing or using our website, you agree to be bound by these Terms.
                                    <br /><br />
                                    If you do not agree with any part of these Terms, please do not use our services.
                              </Text>

                              {/* 1. Definitions */}
                              <Title className={classes.sectionTitle}>1. Definitions</Title>
                              <List spacing="xs" size="sm" center className={classes.list}>
                                    <List.Item><strong>“Company”, “We”, “Us”, “Our”</strong> refers to CDP3D</List.Item>
                                    <List.Item><strong>“User”, “You”, “Customer”</strong> refers to anyone using the website or services</List.Item>
                                    <List.Item><strong>“Design Files”</strong> refers to 3D files uploaded by users (STL, STEP, CAD, etc.)</List.Item>
                              </List>

                              {/* 2. Eligibility */}
                              <Title className={classes.sectionTitle}>2. Eligibility</Title>
                              <Text className={classes.content}>
                                    You must be at least 18 years old and legally capable of entering into a binding contract under Indian law to use our services.
                              </Text>

                              {/* 3. Services Offered */}
                              <Title className={classes.sectionTitle}>3. Services Offered</Title>
                              <Text className={classes.content}>CDP3D provides on-demand 3D printing services, including:</Text>
                              <List spacing="xs" size="sm" center className={classes.list} icon={<ThemeIcon color="cyan" variant="filled" size={16} radius="xl"><IconCheck size="0.9rem" /></ThemeIcon>}>
                                    <List.Item>Upload of 3D design files</List.Item>
                                    <List.Item>Selection of materials and specifications</List.Item>
                                    <List.Item>Manufacturing and delivery of printed products</List.Item>
                              </List>
                              <Text className={classes.content} mt="xs">We reserve the right to refuse service at our discretion.</Text>

                              {/* 4. User Responsibilities */}
                              <Title className={classes.sectionTitle}>4. User Responsibilities</Title>
                              <Text className={classes.content}>By using our services, you confirm that:</Text>
                              <List spacing="xs" size="sm" center className={classes.list} icon={<ThemeIcon color="orange" variant="light" size={16} radius="xl"><IconAlertCircle size="0.9rem" /></ThemeIcon>}>
                                    <List.Item>You own the uploaded design files or have legal rights to use them</List.Item>
                                    <List.Item>The designs do not violate any intellectual property rights, laws, or regulations</List.Item>
                                    <List.Item>The designs do not contain illegal, prohibited, or harmful content</List.Item>
                              </List>
                              <Text className={classes.content} mt="xs">You are solely responsible for the accuracy and legality of the uploaded files.</Text>

                              {/* 5. Intellectual Property */}
                              <Title className={classes.sectionTitle}>
                                    <ThemeIcon color="pink" variant="light" radius="xl"><IconLock size="1rem" /></ThemeIcon>
                                    5. Intellectual Property & Design Files
                              </Title>
                              <List spacing="xs" size="sm" mt="xs" className={classes.list}>
                                    <List.Item>Customers retain full ownership of their uploaded design files.</List.Item>
                                    <List.Item>CDP3D uses uploaded files solely for manufacturing the requested products.</List.Item>
                                    <List.Item>We do not claim ownership over customer designs.</List.Item>
                                    <List.Item>We may refuse to print designs that infringe IP, are illegal, dangerous, or unethical.</List.Item>
                              </List>

                              {/* 6. Pricing & Payments */}
                              <Title className={classes.sectionTitle}>
                                    <ThemeIcon color="green" variant="light" radius="xl"><IconCreditCard size="1rem" /></ThemeIcon>
                                    6. Pricing & Payments
                              </Title>
                              <List spacing="xs" size="sm" mt="xs" className={classes.list}>
                                    <List.Item>All prices are listed in Indian Rupees (INR) unless stated otherwise</List.Item>
                                    <List.Item>Payments must be made in full before order processing</List.Item>
                                    <List.Item>Payments are processed through secure third-party payment gateways</List.Item>
                                    <List.Item>CDP3D does not store payment card or banking details</List.Item>
                                    <List.Item>Prices may change without prior notice</List.Item>
                              </List>

                              {/* 7. Order Acceptance & Manufacturing */}
                              <Title className={classes.sectionTitle}>7. Order Acceptance & Manufacturing</Title>
                              <List spacing="xs" size="sm" mt="xs" className={classes.list}>
                                    <List.Item>An order is considered accepted only after successful payment</List.Item>
                                    <List.Item>Manufacturing begins once files and specifications are confirmed</List.Item>
                                    <List.Item>Due to the custom nature of 3D printing, minor variations in finish, color, or dimensions may occur</List.Item>
                              </List>

                              {/* 8. Cancellations, Refunds & Returns */}
                              <Title className={classes.sectionTitle}>8. Cancellations, Refunds & Returns</Title>
                              <List spacing="xs" size="sm" mt="xs" className={classes.list}>
                                    <List.Item>Orders cannot be cancelled once printing has started.</List.Item>
                                    <List.Item>Custom-made 3D printed products are non-returnable and non-refundable.</List.Item>
                                    <List.Item>Refunds may be issued only if damaged during shipping or manufacturing defect.</List.Item>
                                    <List.Item>Claims must be raised within 7 days of delivery with supporting evidence.</List.Item>
                              </List>

                              {/* 9. Delivery & Shipping */}
                              <Title className={classes.sectionTitle}>
                                    <ThemeIcon color="blue" variant="light" radius="xl"><IconTruck size="1rem" /></ThemeIcon>
                                    9. Delivery & Shipping
                              </Title>
                              <Text className={classes.content}>
                                    Delivery timelines are estimates and may vary due to logistics or unforeseen circumstances.
                                    CDP3D is not liable for delays caused by courier partners, force majeure, or incorrect address details provided by the user.
                              </Text>

                              {/* 10. Limitation of Liability */}
                              <Title className={classes.sectionTitle}>10. Limitation of Liability</Title>
                              <Text className={classes.content}>
                                    To the maximum extent permitted by law, CDP3D shall not be liable for indirect, incidental, or consequential damages. Our total liability shall not exceed the amount paid by the customer for the specific order.
                              </Text>

                              {/* 11. Prohibited Uses */}
                              <Title className={classes.sectionTitle}>11. Prohibited Uses</Title>
                              <Text className={classes.content}>You agree not to use the website or services for:</Text>
                              <List spacing="xs" size="sm" center className={classes.list}>
                                    <List.Item>Illegal or unauthorized purposes</List.Item>
                                    <List.Item>Uploading harmful, offensive, or prohibited designs</List.Item>
                                    <List.Item>Attempting to compromise website security or functionality</List.Item>
                              </List>

                              {/* 12. Termination */}
                              <Title className={classes.sectionTitle}>12. Termination</Title>
                              <Text className={classes.content}>
                                    We reserve the right to suspend or terminate access to our services without notice if these Terms are violated.
                              </Text>

                              {/* 13. Privacy */}
                              <Title className={classes.sectionTitle}>13. Privacy</Title>
                              <Text className={classes.content}>
                                    Your use of our website is also governed by our <Anchor href="/privacy-policy" color="cyan">Privacy Policy</Anchor>.
                              </Text>

                              {/* 14. Governing Law & Jurisdiction */}
                              <Title className={classes.sectionTitle}>
                                    <ThemeIcon color="orange" variant="light" radius="xl"><IconGavel size="1rem" /></ThemeIcon>
                                    14. Governing Law & Jurisdiction
                              </Title>
                              <Text className={classes.content}>
                                    These Terms shall be governed by and interpreted in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in New Delhi.
                              </Text>

                              {/* 15. Changes */}
                              <Title className={classes.sectionTitle}>15. Changes to Terms</Title>
                              <Text className={classes.content}>
                                    CDP3D may update these Terms at any time. Continued use of the website after changes constitutes acceptance of the updated Terms.
                              </Text>

                              {/* 16. Contact */}
                              <Title className={classes.sectionTitle}>16. Contact Information</Title>
                              <Stack spacing={4}>
                                    <Text className={classes.content}><strong>CDP3D</strong></Text>
                                    <Text className={classes.content}><strong>Email:</strong> cdpthreed@gmail.com</Text>
                                    <Text className={classes.content}><strong>Address:</strong> 184-A, Gali No. 3, Padam Nagar, Delhi-110007</Text>
                              </Stack>

                              <Title className={classes.sectionTitle}>✅ Acceptance</Title>
                              <Text className={classes.content}>
                                    By using our website and services, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.
                              </Text>

                        </Card>
                  </Container>
            </div>
      );
}
